import Grid from '@mui/material/Grid';
import { AppStore, GooglePlay, QRCode } from '@packages/shared-ui/components';
import Button, { ButtonVariant } from '@/components/BaseLibrary/Button/Button';
import { mapLegacyButtonVariant } from '@/components/BaseLibrary/Button/mapLegacyButtonVariant';
import EligibilityCheckForm, {
  type StrandedIntakeContent,
} from '@/components/HighlightMediaText/EligibilityCheckForm/EligibilityCheckForm';
import FeatureItems from '@/components/HighlightMediaText/FeatureItems/FeatureItems';
import type { HighlightIconText } from '@/components/HighlightMediaText/HighlightMediaText';
import Link from '@/components/Link/Link';
import type { ButtonContent } from '@/components/OldButton/OldButton';
import SectionHeader from '@/components/SectionHeader/SectionHeader';
import type { Contentful } from '@/contentful/contentful-exports';
import useGlobalStyles from '@/styles/global.styles';
import { brandColors } from '@/styles/theme/values/brandColors';

import useStyles from './TextContent.styles';

export interface TextContentAppLinks extends Contentful {
  fields: {
    appStoreLink: string;
    googlePlayLink: string;
    universalRedirectLink: string;
  };
}

interface TextContentProps {
  subhead?: string;
  features?: HighlightIconText[];
  cta?: ButtonContent[];
  appLinks?: TextContentAppLinks;
  intake?: StrandedIntakeContent;
  isHeroMode?: boolean;
  eyebrow?: string;
  headline?: string;
  isOnDark: boolean;
  gridSizes?: { lg?: number; md?: number; sm?: number; xs?: number };
  isFixed?: boolean;
  contentClassName?: string;
  subheadClassName?: string;
}

const TextContent = ({
  subhead,
  isOnDark,
  eyebrow,
  features,
  headline,
  cta,
  appLinks,
  intake,
  isHeroMode,
  gridSizes,
  isFixed = false,
  contentClassName,
  subheadClassName,
}: TextContentProps): React.JSX.Element => {
  const { classes: globalClasses } = useGlobalStyles({ fixed: isFixed });
  const { classes, cx } = useStyles();
  const { lg, md, sm, xs } = {
    lg: gridSizes?.lg || 5,
    md: gridSizes?.md || 4,
    sm: gridSizes?.sm || 7,
    xs: gridSizes?.xs || 12,
  };

  const intakeFields = intake?.fields;
  const { appStoreLink, googlePlayLink, universalRedirectLink } =
    appLinks?.fields || {};

  return (
    <Grid
      className={cx(classes.content, contentClassName)}
      size={{
        lg,
        md,
        sm,
        xs,
      }}
    >
      <div>
        <div
          style={{
            color: isOnDark
              ? brandColors.tintsOffWhite40
              : brandColors.tintsNeutral20,
          }}
        >
          <SectionHeader
            eyebrow={eyebrow}
            heading={headline}
            isOnDark={isOnDark}
            className={classes.headline}
            fixed={isFixed}
            isH1={isHeroMode}
          />
        </div>
        {subhead && (
          <div
            style={{
              color: isOnDark
                ? brandColors.tintsOffWhite40
                : brandColors.tintsNeutral20,
            }}
            className={cx(globalClasses.body, classes.body, subheadClassName)}
          >
            {subhead}
          </div>
        )}
      </div>
      {Boolean(features?.length) && (
        <FeatureItems features={features} isOnDark={isOnDark} />
      )}
      {intakeFields && <EligibilityCheckForm content={intakeFields} />}
      {!intakeFields && (cta || appLinks) && (
        <div className={classes.ctaContainer}>
          {cta &&
            !appLinks &&
            cta.map((button, index) => {
              const isPrimary = index === 0;
              const buttonContent = button?.fields;
              const defaultVariant = isPrimary
                ? ButtonVariant.SECONDARY
                : ButtonVariant.TERTIARY;
              // Only let the CMS value upgrade a CTA to the solid/high-contrast
              // look (the reported gap). Any other mapped value keeps the
              // existing positional default so unrelated CTAs (e.g. a second
              // button, or content sections outside heroes) don't change
              // appearance based on a legacy `variant` value that used to be
              // ignored entirely.
              const mappedVariant = mapLegacyButtonVariant(
                buttonContent?.variant,
                defaultVariant
              );
              return (
                <Button
                  // biome-ignore lint/style/useTemplate: Automated migration to Biome
                  // biome-ignore lint/suspicious/noArrayIndexKey: Automated migration to Biome
                  key={'button-id' + index + button?.sys?.id}
                  isOnDark={isOnDark}
                  variant={
                    mappedVariant === ButtonVariant.PRIMARY
                      ? mappedVariant
                      : defaultVariant
                  }
                  label={buttonContent?.label}
                  href={buttonContent.url}
                  videoUrl={buttonContent.videoUrl}
                />
              );
            })}
          {appLinks && (
            <div className={classes.qrCodeContainer}>
              <div>
                {universalRedirectLink && (
                  <QRCode
                    data={universalRedirectLink}
                    size={120}
                    alt='QR Code'
                    isOnDark={isOnDark}
                  />
                )}
              </div>
              <div className={classes.appLinks}>
                <div>
                  <Link
                    href={appStoreLink}
                    aria-label='visit apple app store link'
                  >
                    <AppStore width={120} name='app-store-icon' />
                  </Link>
                </div>
                <div>
                  <Link
                    href={googlePlayLink}
                    aria-label='visit google play link'
                  >
                    <GooglePlay width={120} name='google-play-icon' />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Grid>
  );
};

export default TextContent;
